import { TargetHandler } from 'app/modules/posts/components/shiftPosts/shiftTargetProfilesSelect/ShiftPostToTarget';
import { useRef, useState } from 'react';
import { v4 } from 'uuid';
import { togglePrimitiveItemInArray } from 'utils/helpers';
import { ShiftPostsTargetValidationError } from '../components/shiftPosts/ShiftTargetProfilesSelect';
import { ListPost } from 'types/PostDetail';
import { useToast } from 'app/hooks/useToast';
import i18n from 'app/utils/i18n';

export type ShiftPostsTargetHandlers = {
  current: Record<string, TargetHandler>;
};

export type ShiftPostsVariant = 'move' | 'duplicate';

export type ShiftPostsTarget = {
  id: string;
  projectId: number | undefined;
  profileId: number | undefined;
  locked?: boolean;
  postIds?: number[];
};

type Errors = Record<string, ShiftPostsTargetValidationError>;

type Props = {
  initialStep?: number;
  initialPosts?: ListPost[];
  onFinish: () => void;
};

export function useShiftPosts(props: Props) {
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>([]);
  const [errors, setErrors] = useState<Errors>({});
  const [targets, setTargets] = useState<ShiftPostsTarget[]>([
    { id: v4(), profileId: undefined, projectId: undefined },
  ]);
  const targetHandlers = useRef<Record<string, TargetHandler>>({});
  const [selectedPosts, setSelectedPosts] = useState<ListPost[]>(
    props?.initialPosts ?? [],
  );
  const [step, setStep] = useState<number>(
    props?.initialStep ?? STEPS.SELECT_POSTS.step,
  );

  const toast = useToast();

  function togglePostStatus(status: number) {
    setSelectedStatuses((prevSelectedStatuses) => {
      return togglePrimitiveItemInArray<number>(status, prevSelectedStatuses);
    });
  }

  function goNext() {
    if (step === STEPS.RESULTS.step) {
      props.onFinish();
      return;
    }

    setStep((prevStep) => {
      const nextStep = prevStep + 1;

      return nextStep > STEPS.RESULTS.step ? STEPS.RESULTS.step : nextStep;
    });
  }

  function goBack() {
    setStep((prevStep) => {
      const nextStep = prevStep - 1;

      return prevStep < STEPS.SELECT_POSTS.step
        ? STEPS.SELECT_POSTS.step
        : nextStep;
    });
  }

  function togglePost(post: ListPost) {
    setSelectedPosts((posts) => {
      if (posts.includes(post)) {
        return posts.filter((selectedPost) => post.id !== selectedPost.id);
      } else {
        return [...posts, post];
      }
    });
  }

  function validateForm() {
    const errors: Record<string, ShiftPostsTargetValidationError> = {};

    Object.values(targetHandlers.current).forEach((handler) => {
      const error = handler.getValidationError();

      if (error.profileId || error.projectId) {
        errors[error.id] = error;
      }
    });

    setErrors(errors);

    return errors;
  }

  function onSubmit() {
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length) {
      if (
        validationErrors[Object.keys(validationErrors)[0]].projectId ||
        validationErrors[Object.keys(validationErrors)[0]].profileId
      ) {
        return toast(i18n.t('validation.requiredFieldsMissing'), 'warning');
      }
    }

    targets.forEach((target) => {
      const handler = targetHandlers.current[target.id];
      if (handler) {
        const ids = target.postIds ?? selectedPosts.map((post) => post.id);
        handler.submitDuplication(ids);
      }
    });

    goNext();
  }

  function getCanGoNext() {
    if (step === STEPS.SELECT_POSTS.step) {
      return !!selectedPosts.length;
    }

    return false;
  }

  function addTarget() {
    setTargets((prev) => [
      ...prev,
      { id: v4(), projectId: undefined, profileId: undefined },
    ]);
  }

  function removeTarget(id: string) {
    setTargets((prev) =>
      prev.length === 1 ? prev : prev.filter((t) => t.id !== id),
    );
  }

  function getCanShowTargetProfiles() {
    return [STEPS.SELECT_TARGET_PROFILES.step, STEPS.RESULTS.step].includes(
      step,
    );
  }

  function getIsLastStep() {
    return step === STEPS.RESULTS.step;
  }

  return {
    selectedPosts,
    selectedStatuses,
    togglePostStatus,
    togglePost,
    selectPosts: setSelectedPosts,
    onSubmit,
    errors,
    goBack,
    goNext,
    canGoNext: getCanGoNext(),
    step,
    targets,
    addTarget,
    setTargets,
    removeTarget,
    targetHandlers,
    canShowTargetProfiles: getCanShowTargetProfiles(),
    isLastStep: getIsLastStep(),
  };
}

export const STEPS = {
  SELECT_POSTS: {
    step: 1,
    get label() {
      return i18n.t('selectPosts');
    },
  },
  SELECT_TARGET_PROFILES: {
    step: 2,
    get label() {
      return i18n.t('selectTargetProfiles');
    },
  },
  RESULTS: {
    step: 3,
    get label() {
      return i18n.t('results');
    },
  },
};
